import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TradexImportService, ImportJob } from 'app/core/services/tradex/tradex-import.service';

interface PipelineStep {
  label: string;
  icon: string;
  stageNames: string[];
}

const PIPELINE_STEPS: PipelineStep[] = [
  { label: 'Reading', icon: 'description', stageNames: ['READING'] },
  { label: 'Scanning', icon: 'find_in_page', stageNames: ['STAGE_1', 'STAGE_1_RETRY', 'CLASSIFIED'] },
  { label: 'Tracing', icon: 'route', stageNames: ['HANDOFF', 'TRANSFORMING', 'STAGE_2', 'STAGE_2_RETRY'] },
  { label: 'Checking', icon: 'fact_check', stageNames: ['VALIDATING'] },
  { label: 'Saving', icon: 'save', stageNames: ['SAVING'] },
];

@Component({
  selector: 'app-tradex-import-status-panel',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, DragDropModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tradex-import-status-panel.component.html',
  styleUrl: './tradex-import-status-panel.component.scss',
})
export class TradexImportStatusPanelComponent implements OnInit, OnDestroy {
  readonly svc = inject(TradexImportService);
  readonly expanded = signal(true);
  readonly steps = PIPELINE_STEPS;

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly doc = inject(DOCUMENT);

  ngOnInit(): void {
    // Move host element to <body> so position:fixed + z-9999 escapes any
    // stacking context created by layout ancestors and sits above CDK overlay dialogs.
    this.doc.body.appendChild(this.el.nativeElement);

  }

  private errorTimers = new Map<string, ReturnType<typeof setTimeout>>();

  readonly pillLabel = computed(() => {
    const active = this.svc.activeCount();
    const total = this.svc.jobs().length;
    if (active > 0) return `${active} import${active !== 1 ? 's' : ''} in progress`;
    return `${total} import${total !== 1 ? 's' : ''} complete`;
  });

  statusBadge(status: string): string {
    const map: Record<string, string> = { QUEUED: 'Queued', PROCESSING: 'In process', DONE: 'Complete', ERROR: 'Failed' };
    return map[status] ?? status;
  }

  badgeClass(status: string): string {
    if (status === 'QUEUED') return 'text-slate-400';
    if (status === 'PROCESSING') return 'text-indigo-500';
    if (status === 'DONE') return 'text-green-600';
    return 'text-red-500';
  }

  fileCountLabel(job: ImportJob): string {
    const n = job.fileNames?.length ?? 0;
    return n ? `${n} doc${n !== 1 ? 's' : ''}` : '';
  }

  stageText(job: ImportJob): string {
    if (job.status === 'QUEUED') return 'Waiting for the pipeline to start…';
    if (job.status === 'PROCESSING') return job.stageMessage ?? 'Pipeline running…';
    if (job.status === 'DONE')
      return job.targetCertId ? 'Documents processed and merged into your certificate.' : 'Your catch certificate has been created successfully.';
    // ERROR — auto-dismiss after 15s
    if (!this.errorTimers.has(job.jobId)) {
      const t = setTimeout(() => this.svc.dismissJob(job.jobId), 15_000);
      this.errorTimers.set(job.jobId, t);
    }
    return 'Processing failed. Use retry or re-upload your files.';
  }

  elapsed(job: ImportJob): string {
    const s = Math.round((job.elapsedMs ?? 0) / 1000);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  private activeStepIndex(job: ImportJob): number {
    if (!job.stageName) return -1;
    return PIPELINE_STEPS.findIndex((s) => s.stageNames.includes(job.stageName!));
  }

  stepClass(job: ImportJob, i: number): string {
    const active = this.activeStepIndex(job);
    if (job.status === 'QUEUED') return 'bg-slate-100 text-slate-300';
    if (active === -1) return i === 0 ? 'bg-indigo-100 text-indigo-400 animate-pulse' : 'bg-slate-100 text-slate-300';
    if (i < active) return 'bg-green-100 text-green-500';
    if (i === active) return 'bg-indigo-500 text-white ring-2 ring-indigo-300 animate-pulse';
    return 'bg-slate-100 text-slate-300';
  }

  stepLabelClass(job: ImportJob, i: number): string {
    const active = this.activeStepIndex(job);
    if (i < active) return 'text-green-500';
    if (i === active) return 'text-indigo-500 font-bold';
    return 'text-slate-500 dark:text-slate-400';
  }

  stepLineClass(job: ImportJob, i: number): string {
    const active = this.activeStepIndex(job);
    if (job.status === 'QUEUED') return 'bg-slate-200';
    if (active === -1) return 'bg-slate-200';
    return i < active ? 'bg-green-300' : 'bg-slate-200';
  }

  ngOnDestroy(): void {
    this.errorTimers.forEach((t) => clearTimeout(t));
    if (this.el.nativeElement.parentNode === this.doc.body) {
      this.doc.body.removeChild(this.el.nativeElement);
    }
  }
}
